import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { MessageCircle, X, Send, ArrowRight, User } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "sr_chat_session";
const HISTORY_KEY = "sr_chat_history";

const getOrCreateSession = () => {
  let s = localStorage.getItem(SESSION_KEY);
  if (!s) {
    s = `s_${Math.random().toString(36).slice(2, 10)}_${Date.now().toString(36)}`;
    localStorage.setItem(SESSION_KEY, s);
  }
  return s;
};

const INITIAL_MSG = {
  role: "assistant",
  text: "I look after enquiries for the studio. Ask about a project, a stay, or a table, or leave a brief and I will make sure Shruthi sees it.",
};

/* All four in the visitor's voice, all four phrased as something they want. */
const QUICK_PROMPTS = [
  "I want to build a home",
  "I have an old building",
  "I want to book a stay",
  "I am opening a venue",
];

export const Chatbot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([INITIAL_MSG]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [showLead, setShowLead] = useState(false);
  const [lead, setLead] = useState({ name: "", email: "", message: "" });
  const [leadSent, setLeadSent] = useState(false);
  const scrollRef = useRef(null);
  const sessionRef = useRef(null);

  useEffect(() => {
    sessionRef.current = getOrCreateSession();
    const saved = localStorage.getItem(HISTORY_KEY);
    if (saved) {
      try {
        const arr = JSON.parse(saved);
        if (Array.isArray(arr) && arr.length > 0) setMessages(arr);
      } catch (_e) {
        // ignore
      }
    }
  }, []);

  useEffect(() => {
    if (messages.length > 1) {
      localStorage.setItem(HISTORY_KEY, JSON.stringify(messages));
    }
  }, [messages]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, sending]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }]);
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/chat`, {
        session_id: sessionRef.current,
        message: q,
      });
      setMessages((m) => [...m, { role: "assistant", text: data.reply || "..." }]);
    } catch (err) {
      console.error(err);
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text:
            "I'm having trouble reaching the studio right now. You can email Shruthi directly at shruthiravioli@gmail.com and she'll respond within 48 hours.",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.name || !lead.email) return;
    try {
      await axios.post(`${API}/chat/lead`, {
        session_id: sessionRef.current,
        ...lead,
      });
      setLeadSent(true);
      setTimeout(() => setShowLead(false), 1800);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      {/* Floating trigger */}
      <button
        type="button"
        aria-label="Chat with Shruthi's studio concierge"
        data-testid="chatbot-trigger"
        data-cursor="hover"
        onClick={() => setOpen(true)}
        /* Icon only below md. The full pill is ~190px wide and was sitting on
           top of the hero's primary CTA on a 375px viewport. */
        className="fixed bottom-6 right-6 md:bottom-8 md:right-8 z-[45] flex items-center justify-center gap-3 w-12 h-12 md:w-auto md:h-auto md:pl-4 md:pr-5 md:py-3 bg-ink text-cream rounded-full shadow-[0_10px_40px_-10px_rgba(28,27,26,0.55)] hover:bg-terracotta transition-colors duration-500"
      >
        <MessageCircle size={16} className="shrink-0" />
        <span className="hidden md:inline label">
          Ask the studio
        </span>
      </button>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 flex items-end md:items-center justify-end p-0 md:p-6"
            data-testid="chatbot-panel"
          >
            <div
              className="absolute inset-0 bg-ink/30 backdrop-blur-sm"
              onClick={() => setOpen(false)}
            />
            <motion.div
              initial={{ y: 40, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 40, opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.55, ease: [0.19, 1, 0.22, 1] }}
              className="relative w-full md:w-[460px] md:max-w-[460px] h-[85vh] md:h-[640px] md:rounded-[2px] bg-cream text-ink shadow-2xl flex flex-col overflow-hidden border-l border-ink/15"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-ink/15 bg-cream">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full overflow-hidden bg-ink/10">
                    <img
                      src="/portfolio/img/shruthi-portrait-crop.jpg"
                      alt="Shruthi Ravi"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div>
                    <p className="label text-ink2">
                      Studio Concierge
                    </p>
                    <p className="font-display text-lg text-ink leading-none mt-0.5">
                      Shruthi's Atelier
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  data-testid="chatbot-close"
                  aria-label="Close"
                  onClick={() => setOpen(false)}
                  className="w-9 h-9 grid place-items-center text-ink hover:text-terracotta transition-colors"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Body */}
              <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto px-5 py-6 space-y-4 bg-cream"
                data-testid="chatbot-messages"
              >
                {messages.map((m, i) => (
                  <div
                    key={i}
                    className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}
                  >
                    <div
                      className={`shrink-0 w-8 h-8 rounded-full grid place-items-center ${
                        m.role === "user"
                          ? "bg-ink text-cream"
                          : "bg-terracotta/15 text-terracotta"
                      }`}
                    >
                      {m.role === "user" ? (
                        <User size={13} />
                      ) : (
                        <span className="font-display text-sm">S</span>
                      )}
                    </div>
                    <div
                      className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed ${
                        m.role === "user"
                          ? "bg-ink text-cream rounded-2xl rounded-tr-none"
                          : "bg-ink/[0.04] text-ink rounded-2xl rounded-tl-none"
                      }`}
                    >
                      {m.text}
                    </div>
                  </div>
                ))}
                {sending && (
                  <div className="flex gap-3">
                    <div className="shrink-0 w-8 h-8 rounded-full bg-terracotta/15 grid place-items-center text-terracotta font-display text-sm">
                      S
                    </div>
                    <div className="px-4 py-3 bg-ink/[0.04] rounded-2xl rounded-tl-none">
                      <div className="flex gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-ink2 animate-pulse" />
                        <span
                          className="w-1.5 h-1.5 rounded-full bg-ink2 animate-pulse"
                          style={{ animationDelay: "0.15s" }}
                        />
                        <span
                          className="w-1.5 h-1.5 rounded-full bg-ink2 animate-pulse"
                          style={{ animationDelay: "0.3s" }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {messages.length === 1 && !sending && (
                  <div className="pt-2 space-y-2">
                    <p className="label text-ink2">
                      Or try
                    </p>
                    {QUICK_PROMPTS.map((q) => (
                      <button
                        key={q}
                        type="button"
                        data-testid={`chatbot-prompt-${q.slice(0, 8).replace(/\s/g, "-")}`}
                        onClick={() => send(q)}
                        className="block text-left w-full px-4 py-3 border border-ink/15 hover:border-ink hover:bg-ink hover:text-cream font-sans text-sm transition-all duration-300"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Lead capture inline */}
              <AnimatePresence>
                {showLead && (
                  <motion.form
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.4 }}
                    onSubmit={submitLead}
                    className="border-t border-ink/15 bg-ink/[0.03] px-5 py-4 space-y-3"
                    data-testid="chatbot-lead-form"
                  >
                    {leadSent ? (
                      <p className="font-sans text-sm text-ink">
                        Thank you. Shruthi will be in touch within 48 hours.
                      </p>
                    ) : (
                      <>
                        <p className="label text-ink2">
                          Leave a brief for Shruthi
                        </p>
                        <input
                          data-testid="chatbot-lead-name"
                          className="editorial-input text-sm py-2"
                          placeholder="Your name"
                          value={lead.name}
                          onChange={(e) => setLead({ ...lead, name: e.target.value })}
                        />
                        <input
                          data-testid="chatbot-lead-email"
                          className="editorial-input text-sm py-2"
                          placeholder="Email"
                          type="email"
                          value={lead.email}
                          onChange={(e) => setLead({ ...lead, email: e.target.value })}
                        />
                        <textarea
                          data-testid="chatbot-lead-message"
                          className="editorial-input text-sm py-2 min-h-[70px]"
                          placeholder="A short brief"
                          value={lead.message}
                          onChange={(e) => setLead({ ...lead, message: e.target.value })}
                        />
                        <div className="flex items-center justify-between">
                          <button
                            type="button"
                            onClick={() => setShowLead(false)}
                            className="label text-ink2 editorial-link"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            data-testid="chatbot-lead-submit"
                            className="inline-flex items-center gap-2 px-4 py-2 bg-ink text-cream text-[10px] tracking-[0.22em] uppercase font-mono hover:bg-terracotta transition-colors"
                          >
                            Send brief <ArrowRight size={12} />
                          </button>
                        </div>
                      </>
                    )}
                  </motion.form>
                )}
              </AnimatePresence>

              {/* Footer / input */}
              <div className="border-t border-ink/15 bg-cream px-4 py-3">
                {!showLead && (
                  <div className="flex items-center justify-between px-2 pb-2">
                    <button
                      type="button"
                      onClick={() => setShowLead(true)}
                      data-testid="chatbot-open-lead"
                      className="label text-ink2 editorial-link"
                    >
                      Leave a brief →
                    </button>
                    <span className="label text-ink2">
                      Powered by the studio
                    </span>
                  </div>
                )}
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    send();
                  }}
                  className="flex items-center gap-2"
                >
                  <input
                    data-testid="chatbot-input"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Ask about her work or ventures…"
                    className="flex-1 px-4 py-3 bg-ink/[0.04] font-sans text-sm text-ink placeholder:text-ink2 outline-none focus:bg-ink/[0.06]"
                  />
                  <button
                    type="submit"
                    data-testid="chatbot-send"
                    disabled={sending || !input.trim()}
                    className="w-11 h-11 grid place-items-center bg-ink text-cream disabled:opacity-40 hover:bg-terracotta transition-colors"
                    aria-label="Send"
                  >
                    <Send size={15} />
                  </button>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
